import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { useMarketing, Campaign } from "../../../context/MarketingContext";
import { fetchCampaignById, mapCampaignFromApi } from "../../../services/marketingTemplatesApi";
import { CampaignForm } from "./CampaignForm";

const EditCampaignPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { campaigns, customers, calculateCreditsNeeded } = useMarketing();

  const fromList = useMemo(
    () => (id ? campaigns.find((c) => c.id === id) : undefined),
    [campaigns, id],
  );

  const [apiRow, setApiRow] = useState<Awaited<ReturnType<typeof fetchCampaignById>> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!id) {
      setApiRow(null);
      return;
    }
    if (fromList) {
      setApiRow(undefined);
      return;
    }
    let cancelled = false;
    setApiRow(undefined);
    fetchCampaignById(id)
      .then((row) => {
        if (!cancelled) setApiRow(row ?? null);
      })
      .catch(() => {
        if (!cancelled) setApiRow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, fromList]);

  const resolved: Campaign | null | undefined = useMemo(() => {
    if (fromList) return fromList;
    if (apiRow === undefined) return undefined;
    if (apiRow === null) return null;
    const core = mapCampaignFromApi(apiRow, customers);
    return {
      ...core,
      creditsUsed: calculateCreditsNeeded({
        selectedCustomerIds: core.selectedCustomerIds,
        useAllCustomers: core.useAllCustomers,
        emailTemplateId: core.emailTemplateId,
        smsTemplateId: core.smsTemplateId,
      }),
    };
  }, [fromList, apiRow, customers, calculateCreditsNeeded]);

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4">
        <h2 className="text-base lg:text-lg font-semibold text-gray-900">
          Edit Campaign
        </h2>
        <p className="text-xs lg:text-sm text-gray-500">
          Adjust the audience or templates used by this campaign.
        </p>
      </div>
      {resolved === undefined && !fromList && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
          <Loader2 className="w-10 h-10 text-[#885ABB] animate-spin" />
          <p className="text-sm">Loading campaign…</p>
        </div>
      )}
      {resolved === null && (
        <p className="text-sm text-red-600">Campaign not found.</p>
      )}
      {resolved && <CampaignForm campaign={resolved} />}
    </div>
  );
};

export default EditCampaignPage;
